import { Link } from "react-router-dom";
import {
  Globe, Sparkles, Search, Layers, Languages, Volume2, Palette,
  Users, TrendingUp, QrCode, Brain, MessageSquare, ShieldCheck,
  Zap, BarChart3, Star, MapPin, Building2, Rocket, ArrowRight,
  Target,
} from "lucide-react";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";
import { PAGE_SEO } from "@/data/pageSeo";

const INFOGRAPHIC = "https://customer-assets-lqy194kg.emergentagent.net/job_web-transform-57/artifacts/643grj5d_websitetech%20infographic.png";

/**
 * /about — WebsiteTech.pro brand + platform overview.
 * Uses the customer-supplied infographic as the anchor visual.
 */
export default function About() {
  usePageSeo(PAGE_SEO["/about"]);
  useBreadcrumbSchema([{ name: "Home", url: "/" }, { name: "About", url: "/about" }]);
  return (
    <div className="min-h-screen bg-slate-950 text-white pb-24" data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-indigo-950 to-purple-950">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-12">
          <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold">
            About WebsiteTech.pro
          </div>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-4xl">
            Better Websites.{" "}
            <span className="bg-gradient-to-r from-fuchsia-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">
              Smarter Marketing.
            </span>
            <br />Practical AI. Measurable Growth.
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-3xl leading-relaxed">
            WebsiteTech.pro helps businesses build a stronger digital presence, attract more customers, automate everyday work, understand what is working, and grow with greater confidence.
          </p>
          <p className="mt-4 text-white/60 max-w-3xl">
            We work with entrepreneurs, local businesses, professionals, consultants, organizations, agencies, and growing companies that want to take advantage of modern websites, digital marketing, artificial intelligence, automation, analytics, and customer-engagement technology — without having to assemble and manage dozens of disconnected tools.
          </p>
          <div className="mt-6 rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur p-5 max-w-3xl">
            <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">Our goal is simple</div>
            <div className="mt-1 text-lg font-bold">
              Make powerful digital and AI technology practical, affordable, understandable, and useful for businesses of every size.
            </div>
          </div>
        </div>
      </section>

      {/* Infographic */}
      <section className="max-w-6xl mx-auto px-6 mt-12" data-testid="about-infographic">
        <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold text-center">
          How it works
        </div>
        <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-center">
          Turn any website into powerful advertising
        </h2>
        <p className="mt-3 text-white/60 max-w-2xl mx-auto text-center">
          From a website URL to ready-to-publish ads — even when the site has no photos. Enter a URL, review, customize, and ship in minutes.
        </p>
        <div className="mt-8 rounded-3xl overflow-hidden ring-1 ring-white/10 bg-white">
          <img src={INFOGRAPHIC} alt="WebsiteTech AI Ad Builder workflow — 8 steps from URL to published ads with QR + short URL"
                className="w-full h-auto block"
                data-testid="about-infographic-img" />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <Link to="/ad-builder"
                data-testid="about-cta-adbuilder"
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white px-5 py-3 font-bold">
            Try AI Ad Builder <Rocket className="w-4 h-4" />
          </Link>
          <Link to="/ad-builder/faq"
                data-testid="about-cta-faq"
                className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm underline">
            Read the FAQ <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </section>

      {/* More than a website */}
      <Section eyebrow="What we do" title="More than a website company">
        <p className="text-white/70 leading-relaxed">
          A website should do more than look good. It should help people discover your business, understand what you offer, trust your organization, contact you easily, and ultimately become customers.
        </p>
        <p className="mt-4 text-white/70 leading-relaxed">
          That is why WebsiteTech goes beyond traditional website design. We combine <b>Website + SEO + Content + AI + Advertising + Social Media + Lead Generation + Automation + Analytics + Customer Engagement</b> into an integrated business-growth platform — instead of asking businesses to purchase and manage a different service for every problem.
        </p>
      </Section>

      {/* Capability grid */}
      <section className="max-w-6xl mx-auto px-6 mt-16">
        <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold text-center">
          Platform capabilities
        </div>
        <h2 className="mt-2 text-3xl font-bold text-center">Everything connected</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-capability-grid">
          {CAPABILITIES.map(c => (
            <div key={c.title} className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 hover:bg-white/[0.07] transition"
                  data-testid={`about-cap-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className={`w-10 h-10 rounded-xl ${c.tint} flex items-center justify-center mb-3`}>
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="font-bold">{c.title}</h3>
              <p className="mt-1 text-sm text-white/60 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Flywheel */}
      <section className="max-w-6xl mx-auto px-6 mt-16">
        <div className="rounded-3xl bg-gradient-to-br from-fuchsia-500/10 via-white/5 to-amber-400/10 ring-1 ring-white/10 backdrop-blur p-8"
             data-testid="about-flywheel">
          <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-300 font-bold text-center">One connected growth ecosystem</div>
          <h2 className="mt-2 text-3xl font-bold text-center">Attract → Engage → Convert → Follow Up → Measure → Learn → Improve</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-7 gap-3 text-center text-sm">
            {[
              ["ATTRACT", "SEO • Content • Social • Advertising"],
              ["ENGAGE", "Website • AI Assistants • Multilingual"],
              ["CONVERT", "Landing pages • Forms • Appointments"],
              ["FOLLOW UP", "Email • Messaging • Automation"],
              ["MEASURE", "Analytics • SpLink • QR • Leads"],
              ["LEARN", "Brand Brain • Audience Brain • Campaign IQ"],
              ["IMPROVE", "AI recommendations • Better campaigns"],
            ].map(([t, s]) => (
              <div key={t} className="rounded-xl bg-white/5 p-3">
                <div className="text-xs font-bold text-fuchsia-300">{t}</div>
                <div className="text-xs text-white/60 mt-1">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <Section eyebrow="Our mission" title="Practical AI for real businesses">
        <p className="text-white/70 leading-relaxed">
          Our mission is to make advanced website technology, artificial intelligence, digital marketing and business automation accessible to organizations of every size. Technology is changing rapidly. Businesses should not have to chase every new AI tool or subscribe to dozens of disconnected services just to keep up.
        </p>
        <p className="mt-4 text-white/70 leading-relaxed">
          We measure progress not by how many AI features we can add, but by whether technology helps businesses:
        </p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-white/80">
          {["Attract more customers","Serve customers better","Save time","Make better-informed decisions","Reduce unnecessary work","Build stronger businesses"].map(x => (
            <li key={x} className="flex items-start gap-2 rounded-lg bg-white/5 px-3 py-2">
              <Star className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" /> {x}
            </li>
          ))}
        </ul>
      </Section>

      {/* Human control */}
      <Section eyebrow="Our philosophy" title="AI-Assisted. Human-Controlled.">
        <p className="text-white/70 leading-relaxed">
          WebsiteTech believes AI should assist people — not quietly make important business decisions for them. AI can analyze, recommend, create, draft, compare, measure, and learn. But important actions such as publishing content, launching paid advertising, or changing advertising budgets remain subject to appropriate human authorization.
        </p>
      </Section>

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-6 mt-16">
        <div className="rounded-3xl bg-gradient-to-r from-fuchsia-500/20 via-orange-400/10 to-amber-400/10 ring-1 ring-fuchsia-400/30 backdrop-blur p-8 text-center"
             data-testid="about-final-cta">
          <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-300 font-bold">Welcome to WebsiteTech.pro</div>
          <h2 className="mt-2 text-3xl font-bold">Build. Attract. Engage. Convert. Measure. Learn. Grow.</h2>
          <p className="mt-3 text-white/70">A new kind of digital business platform — where your website, marketing, content, advertising, customer engagement, analytics and AI systems work together rather than separately.</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/ad-builder"
                  className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 hover:brightness-110 text-white px-5 py-3 font-bold">
              Start free <Rocket className="w-4 h-4" />
            </Link>
            <Link to="/social-studio"
                  className="inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 text-white px-5 py-3 font-bold">
              Social Studio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


function Section({ eyebrow, title, children }) {
  return (
    <section className="max-w-4xl mx-auto px-6 mt-16">
      <div className="text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold">{eyebrow}</div>
      <h2 className="mt-2 text-3xl font-bold">{title}</h2>
      <div className="mt-4">{children}</div>
    </section>
  );
}


const CAPABILITIES = [
  { title: "Websites",              icon: Globe,          tint: "bg-sky-500/20 text-sky-300",
    body: "Modern responsive sites, landing pages, e-commerce, appointment workflows, accessibility & performance." },
  { title: "AI Website Intelligence", icon: Brain,        tint: "bg-fuchsia-500/20 text-fuchsia-300",
    body: "Understand structure, content quality, customer journeys, conversion barriers — in plain language." },
  { title: "SEO",                   icon: Search,         tint: "bg-emerald-500/20 text-emerald-300",
    body: "Audits, keywords, schema, local SEO, competitor analysis, and continuous monitoring." },
  { title: "AI Content Studio",     icon: Layers,         tint: "bg-amber-500/20 text-amber-300",
    body: "Pages, articles, product descriptions, FAQs, social posts, ad copy — with human review." },
  { title: "Multilingual",          icon: Languages,      tint: "bg-cyan-500/20 text-cyan-300",
    body: "AI-assisted translation with language-specific URLs, SEO, and update tracking." },
  { title: "Listen to content",     icon: Volume2,        tint: "bg-indigo-500/20 text-indigo-300",
    body: "Text-to-speech for accessibility and mobile listening." },
  { title: "AI Visual Campaign Studio", icon: Palette,   tint: "bg-rose-500/20 text-rose-300",
    body: "URL → brand → audience → concepts → graphics → multiple formats → publish or export." },
  { title: "Brand Brain",           icon: Sparkles,       tint: "bg-purple-500/20 text-purple-300",
    body: "Logos, colors, voice, approved terminology — locked so AI stays on brand." },
  { title: "Audience Brain",        icon: Users,          tint: "bg-orange-500/20 text-orange-300",
    body: "Understand who you're reaching and refine as campaign data grows." },
  { title: "Campaign Management",   icon: Target,         tint: "bg-blue-500/20 text-blue-300",
    body: "Plan, approve, budget-control, monitor, and compare creatives." },
  { title: "SpLink + QR",           icon: QrCode,         tint: "bg-teal-500/20 text-teal-300",
    body: "Campaign-specific short URLs and QR codes so every channel is measurable." },
  { title: "Campaign Intelligence", icon: TrendingUp,     tint: "bg-lime-500/20 text-lime-300",
    body: "Learn from previous campaigns. Ask questions in plain English." },
  { title: "Lead Generation",       icon: MessageSquare,  tint: "bg-yellow-500/20 text-yellow-300",
    body: "Turn traffic into inquiries, appointments, and customers." },
  { title: "AI Customer Assistants",icon: MessageSquare,  tint: "bg-fuchsia-500/20 text-fuchsia-300",
    body: "Helpful on-site assistants for navigation, FAQs, and inquiries." },
  { title: "Follow-up & Automation",icon: Zap,            tint: "bg-amber-500/20 text-amber-300",
    body: "Reminders, review requests, nurture flows, internal notifications." },
  { title: "Analytics",             icon: BarChart3,      tint: "bg-emerald-500/20 text-emerald-300",
    body: "Business questions, not chart-overload. Measure → Understand → Improve." },
  { title: "Reputation & Trust",    icon: ShieldCheck,    tint: "bg-sky-500/20 text-sky-300",
    body: "Reviews, testimonials, credentials, case studies, monitoring." },
  { title: "Local Business Growth", icon: MapPin,         tint: "bg-rose-500/20 text-rose-300",
    body: "Doctors, restaurants, contractors, farms, tourism, professionals — all served." },
  { title: "Enterprise Services",   icon: Building2,      tint: "bg-slate-500/20 text-slate-300",
    body: "Readiness assessments, content audits, competitive intelligence, multilingual strategy." },
];
